import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { t } from '../../../../modules/infra/i18n';

import withToggle from '../../../common/hoc/withToggle';
import { RefundsReport, DiscountsReport } from '../refundsAndDiscounts';
import { RoleDenyBrand, RoleAllowBrand } from '../../../auth/roleAware';
import { MoneyFitSmall } from './MoneyFit';

const NetSales = ({
  totalSales,
  refunds,
  discounts,
  netSales,
  costOfInventory,
  showGrossProfit,
  grossProfit,
  showCostOfInventory,
  refundsIsOn,
  refundsOn,
  refundsOff,
  discountsIsOn,
  discountsOn,
  discountsOff,
  kind,
}) => (
  <div>
    <RefundsReport open={refundsIsOn} close={refundsOff} kind={kind} />
    <DiscountsReport open={discountsIsOn} close={discountsOff} kind={kind} />
    <div className="c-report-total-calculation u-pv medium-and-down-pv+ u-ph extralarge-and-up-ph++">
      <div className="c-report-total-calculation__cell u-pl">
        <div className="c-report-label">{t('reports:net_sales.total_gross_sales')}</div>
        <div className="c-report-stat c-report-stat--small u-type--normal">
          <MoneyFitSmall automationId="text:netSales:TotalAmount">{totalSales}</MoneyFitSmall>
        </div>
      </div>
      <div className="c-report-total-calculation__cell">
        <div className="c-report-stat c-report-stat--small u-co-gray-1 u-mr0 medium-and-up-mr+ u-pv">-</div>
      </div>
      <div className="c-report-total-calculation__cell">
        <div className="c-report-label">{t('reports:net_sales.refunds')}</div>
        <RoleDenyBrand>
          <div
            className="c-report-stat c-report-stat--small u-type--normal u-type--underline u-cursor-pointer"
            onClick={refundsOn}
          >
            <MoneyFitSmall automationId="text:netSales:RefundsAmount">{refunds}</MoneyFitSmall>
          </div>
        </RoleDenyBrand>
        <RoleAllowBrand>
          <div className="c-report-stat c-report-stat--small u-type--normal">
            <MoneyFitSmall automationId="text:netSales:RefundsAmount">{refunds}</MoneyFitSmall>
          </div>
        </RoleAllowBrand>
      </div>
      <div className="c-report-total-calculation__cell">
        <div className="c-report-stat c-report-stat--small u-co-gray-1 u-pv">-</div>
      </div>
      <div className="c-report-total-calculation__cell">
        <div className="c-report-label">
          {t('reports:net_sales.discounts')}
          <div className="c-info-tooltip-new c-info-tooltip-new-handler u-ml--">
            <div className="c-info-tooltip-new__content">
              {t('reports:net_sales.discounts_tooltip')}
            </div>
          </div>
        </div>
        <RoleDenyBrand>
          <div
            className="c-report-stat c-report-stat--small u-type--normal u-type--underline u-cursor-pointer"
            onClick={discountsOn}
          >
            <MoneyFitSmall automationId="text:netSales:DiscountsAmount">{discounts}</MoneyFitSmall>
          </div>
        </RoleDenyBrand>
        <RoleAllowBrand>
          <div className="c-report-stat c-report-stat--small u-type--normal">
            <MoneyFitSmall automationId="text:netSales:DiscountsAmount">{discounts}</MoneyFitSmall>
          </div>
        </RoleAllowBrand>
      </div>
      <div className="c-report-total-calculation__cell">
        <div className="c-report-stat c-report-stat--small u-co-gray-1 u-ml+ medium-and-down-ml0 u-pv">=</div>
      </div>
      <div className="c-report-total-calculation__cell u-pr">
        <div className="c-report-label">{t('reports:net_sales.net_sales')}</div>
        <div className="c-report-stat c-report-stat--small u-type--normal">
          <MoneyFitSmall automationId="text:netSales:netSalesAmount">{netSales}</MoneyFitSmall>
        </div>
      </div>
      <div className="c-report-total-calculation__cell" display-if={showCostOfInventory}>
        <div className="c-report-stat c-report-stat--small u-co-gray-1 u-pv">-</div>
      </div>
      <div className="c-report-total-calculation__cell" display-if={showCostOfInventory}>
        <div className="c-report-label">{t('reports:net_sales.cost_of_inventory')}</div>
        <div className="c-report-stat c-report-stat--small u-type--normal">
          <MoneyFitSmall automationId="text:netSales:ConsOfInventoryAmount">{costOfInventory}</MoneyFitSmall>
        </div>
      </div>
      <div className="c-report-total-calculation__cell" display-if={showGrossProfit}>
        <div className="c-report-stat c-report-stat--small u-co-gray-1 u-ml+ medium-and-down-ml0 u-pv">=</div>
      </div>
      <div className="c-report-total-calculation__cell u-pr" display-if={showGrossProfit}>
        <div className="c-report-label">{t('reports:net_sales.gross_profit')}</div>
        <div className="c-report-stat c-report-stat--small u-type--normal">
          <MoneyFitSmall automationId="text:netSales:GrossProfitAmount">{grossProfit}</MoneyFitSmall>
        </div>
      </div>
    </div>
  </div>
);

NetSales.propTypes = {
  totalSales: PropTypes.number.isRequired,
  refunds: PropTypes.number.isRequired,
  discounts: PropTypes.number.isRequired,
  netSales: PropTypes.number.isRequired,
  showGrossProfit: PropTypes.bool,
  grossProfit: PropTypes.number,
  costOfInventory: PropTypes.number,
  showCostOfInventory: PropTypes.bool,
  refundsIsOn: PropTypes.bool.isRequired,
  refundsOn: PropTypes.func.isRequired,
  refundsOff: PropTypes.func.isRequired,
  discountsIsOn: PropTypes.bool.isRequired,
  discountsOn: PropTypes.func.isRequired,
  discountsOff: PropTypes.func.isRequired,
  kind: PropTypes.string,
};

NetSales.defaultProps = {
  showGrossProfit: false,
  showCostOfInventory: false,
  grossProfit: null,
  costOfInventory: null,
  kind: null,
};

export default compose(withToggle('refunds', false), withToggle('discounts', false))(NetSales);
